import { useEffect } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import {
  isExcludeAcquiredLocked,
  type BulkAcquireFormData,
} from "../utils/bulkAcquireFormSchema";
import { usePolicyFromForm } from "./useBulkAcquireForm";
import { type AcquirePolicy } from "../utils/acquirePolicy";
import { useCommittedPastDays } from "./useCommittedPastDays";

export interface BulkAcquireConditions {
  policy: AcquirePolicy;
  /** セルの可否判定・内訳に使う確定済みの経過日数 */
  committedPastDays: number;
  pastDaysFocusProps: ReturnType<
    typeof useCommittedPastDays
  >["pastDaysFocusProps"];
  excludeAcquired: boolean;
  /** 未取得のみの指定を受け付けない条件（値も解除される） */
  excludeAcquiredLocked: boolean;
}

/**
 * 取得方法フォームからグリッドの可否判定・内訳・送信に使う取得条件を導く。
 * 値の変更で可視セルだけ再評価される
 */
export const useBulkAcquireConditions = (
  form: UseFormReturn<BulkAcquireFormData>
): BulkAcquireConditions => {
  const { policy } = usePolicyFromForm(form.control);
  const pastDays = useWatch({ control: form.control, name: "pastDays" });
  const { committedPastDays, pastDaysFocusProps } =
    useCommittedPastDays(pastDays);
  const excludeAcquired = useWatch({
    control: form.control,
    name: "excludeAcquired",
  });

  // 指定を受け付けない条件では値も解除する（チェックを残したまま非活性にすると、指定が効いているように見える）
  const mode = useWatch({ control: form.control, name: "mode" });
  const excludeAcquiredLocked = isExcludeAcquiredLocked(mode, policy);
  useEffect(() => {
    if (excludeAcquiredLocked) {
      form.setValue("excludeAcquired", false);
    }
  }, [excludeAcquiredLocked, form]);

  return {
    policy,
    committedPastDays,
    pastDaysFocusProps,
    excludeAcquired,
    excludeAcquiredLocked,
  };
};
